/**
 * Click & Collect – Cart Page
 *
 * When the "Click & Collect" delivery radio is selected a popup opens
 * listing every Shopify Location where **all** current cart‑items are
 * available for pick‑up. Selecting a store saves it in the cart note.
 *
 * IMPORTANT: The delivery‑options radios and selected‑store banner live
 * inside `.js-contents` which gets innerHTML‑replaced on every cart
 * AJAX update. This script therefore:
 *   – Uses document‑level event delegation (never binds to elements
 *     that will be destroyed).
 *   – Subscribes to PUB_SUB_EVENTS.cartUpdate to invalidate the store
 *     cache (cart items may have changed) and restore UI state.
 *   – The popup itself lives OUTSIDE `.js-contents` so it persists.
 */
(function () {
  'use strict';

  /* ── constants ─────────────────────────────────────────────── */
  const POPUP_ID = 'ClickCollectPopup';
  const OVERLAY_ID = 'ClickCollectOverlay';
  const SELECTED_STORE_ID = 'ClickCollectSelectedStore';
  const SESSION_KEY = 'cc_selected_store';
  const SESSION_KEY_ID = 'cc_selected_store_id';
  const STOREFRONT_API_VERSION = '2026-04';

  const SEARCH_RADIUS_MILES = 1000;

  /* ── state (persists across section re‑renders) ────────────── */
  let allStores = [];
  let filteredStores = [];
  let selectedStore = null;
  let confirmedStore = null;
  let fetched = false;
  let listenersAttached = false;
  let storeCoords = null; // loaded once from store-locations.json
  let fetchGeneration = 0; // incremented on each cart change to cancel in-flight fetches


  /* ── helpers ───────────────────────────────────────────────── */
  const qs = (sel, root) => (root || document).querySelector(sel);
  const qsa = (sel, root) => [...(root || document).querySelectorAll(sel)];

  /* ── store coordinates (from shop metafield custom.locations_mapping) */
  // Supports two metafield formats (can be mixed):
  //   ID-based (preferred): { "id": "70354960481", "lat": -5.16, "lng": 119.39 }
  //   Name-based (legacy):  { "name": "STORE NAME",  "lat": -5.16, "lng": 119.39 }
  // The pickup-availability section injects data-location-id via Liquid so the
  // JS can match by ID — no Storefront API token required.

  function loadStoreCoords() {
    if (storeCoords) return Promise.resolve(storeCoords);

    const popup = document.getElementById(POPUP_ID);
    const raw = popup ? popup.dataset.storeLocations : null;

    if (!raw) {
      storeCoords = [];
      return Promise.resolve(storeCoords);
    }

    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      parsed = [];
    }

    // Pass coords as-is — entries may have { id, lat, lng } or { name, lat, lng } or both.
    // findCoords() matches by locationId (from pickup-availability HTML) first,
    // then falls back to name matching for legacy entries.
    storeCoords = parsed;
    console.log('[Click & Collect] loadStoreCoords → loaded', storeCoords.length, 'entries', storeCoords);
    return Promise.resolve(storeCoords);
  }

  /* ── Haversine distance (km) ───────────────────────────────── */
  function haversineMiles(lat1, lng1, lat2, lng2) {
    const R = 3959;
    const toRad = (v) => (v * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    console.log('[Click & Collect] haversineMiles →', {lat1, lng1, lat2, lng2, distMiles: dist});
    return dist;
  }

  /* ── Nominatim geocoder (free, no API key) ─────────────────── */
  function geocodePincode(query) {
    const encoded = encodeURIComponent(query + ', Indonesia');
    const url = `https://nominatim.openstreetmap.org/search?q=${encoded}&format=json&limit=1`;

    return fetch(url, { headers: { 'Accept-Language': 'en' } })
      .then((r) => r.json())
      .then((results) => {
        console.log('[Click & Collect] geocodePincode → raw API response:', results);
        if (results.length === 0) {
          console.warn('[Click & Collect] geocodePincode → no results for query:', query);
          return null;
        }
        const loc = {
          lat: parseFloat(results[0].lat),
          lng: parseFloat(results[0].lon),
        };
        console.log('[Click & Collect] geocodePincode → resolved lat/lng:', loc);
        return loc;
      })
      .catch((err) => {
        console.error('[Click & Collect] geocodePincode → error:', err);
        return null;
      });
  }

  /* ── fetch stores for a single variant ─────────────────────── */
  function fetchStoresForVariant(variantId, rootUrl) {
    if (!rootUrl.endsWith('/')) rootUrl += '/';
    const url = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

    return fetch(url)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const items = doc.querySelectorAll('.pickup-availability-list__item');
        const stores = [];
        items.forEach((li) => {
          const nameEl = li.querySelector('h3');
          const addressEl = li.querySelector('.pickup-availability-address');
          const isAvailable = !!li.querySelector('.icon-tick');
          if (nameEl) {
            stores.push({
              name: (nameEl.textContent || '').trim(),
              address: addressEl ? addressEl.innerHTML.trim() : '',
              addressText: addressEl ? (addressEl.textContent || '').trim() : '',
              available: isAvailable,
              locationId: li.dataset.locationId || null,
            });
          }
        });
        return stores;
      })
      .catch(() => []);
  }

  /* ── intersect stores across all variants ──────────────────── */
  function intersectStores(storeArrays) {
    if (storeArrays.length === 0) return [];
    if (storeArrays.length === 1) return storeArrays[0].filter((s) => s.available);

    const first = storeArrays[0];
    return first.filter((store) => {
      if (!store.available) return false;
      return storeArrays.every((arr) =>
        arr.some((s) => s.name === store.name && s.available)
      );
    });
  }

  /* ── cart data ─────────────────────────────────────────────── */
  function getCartItems() {
    return fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) =>
        cart.items.map((item) => ({
          variantId: item.variant_id,
          productUrl: item.url.split('?')[0],
          handle: item.handle,
        }))
      );
  }

  function getRootUrl() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }


  /* ── store selection helpers ───────────────────────────────── */
  // Strategy:
  //   • sessionStorage  – instant cart-page UI restoration (no API round-trip).
  //   • cart attributes – passes pickup_location to the checkout delivery-
  //                       customization extension so it can pre-select the store.
  //   • cart note       – deliberately NOT written; this is what causes the
  //                       "[Click & Collect Store: …]" text to appear in the
  //                       order's "Notes" section, so we omit it entirely.

  function saveStoreToCartNote(storeName, locationId) {
    try {
      sessionStorage.setItem(SESSION_KEY, storeName);
      if (locationId) sessionStorage.setItem(SESSION_KEY_ID, String(locationId));
    } catch (e) {}
    return fetch(
      (window.Shopify?.routes?.root || '/') + 'cart/update.js',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Underscore-prefixed keys are private in Shopify — readable by
          attributes: {
            _pickup_location: storeName,
            _pickup_location_id: locationId ? String(locationId) : '',
          }
        }),
      }
    ).then((r) => r.json()).catch(() => {});
  }

  function clearStoreFromCartNote() {
    try {
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(SESSION_KEY_ID);
    } catch (e) {}
    return fetch(
      (window.Shopify?.routes?.root || '/') + 'cart/update.js',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { _pickup_location: '', _pickup_location_id: '' }
        }),
      }
    ).then((r) => r.json()).catch(() => {});
  }

  /* ── Storefront API: cart delivery preference ──────────────── */
  function getCartToken() {
    return fetch('/cart.js').then((r) => r.json()).then((cart) => cart.token).catch(() => null);
  }

  /**
   * Updates the cart's buyerIdentity delivery preference via the Storefront API.
   * deliveryMethod: 'PICK_UP' when selecting Click & Collect, 'SHIPPING' to clear.
   * locationId: numeric Shopify location ID (from data-location-id on pickup-availability items).
   * Silently no-ops if the storefront token is not configured in theme settings.
   */
  function updateCartDeliveryPreference(cartToken, deliveryMethod, locationId) {
    const popup = document.getElementById(POPUP_ID);
    const shopDomain = popup ? popup.dataset.shopDomain : null;
    const storefrontToken = popup ? popup.dataset.storefrontToken : null;

    if (!shopDomain || !storefrontToken || !cartToken) {
      if (!storefrontToken) {
        console.warn('[Click & Collect] storefront_api_token not configured in theme settings (Theme settings > Click & Collect). Pickup cannot be pre-selected at checkout without it.');
      }
      return Promise.resolve(null);
    }

    const cartGid = 'gid://shopify/Cart/' + cartToken;
    const deliveryInput = { deliveryMethod: [deliveryMethod] };
    if (deliveryMethod === 'PICK_UP' && locationId) {
      // Use the delivery-option handle directly (numeric location ID for function-generated
      // options). The GID format "gid://shopify/Location/…" does NOT match the handle that
      // the custom delivery-option-generator function returns, so checkout would open the
      // Pickup tab but leave no location pre-selected.
      deliveryInput.pickupHandle = [String(locationId)];
    }

    const mutation =
      'mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {' +
      '  cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {' +
      '    cart { id checkoutUrl }' +
      '    userErrors { field message }' +
      '  }' +
      '}';

    return fetch('https://' + shopDomain + '/api/' + STOREFRONT_API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontToken,
      },
      body: JSON.stringify({
        query: mutation,
        variables: {
          cartId: cartGid,
          buyerIdentity: { preferences: { delivery: deliveryInput } },
        },
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        const result = data.data && data.data.cartBuyerIdentityUpdate;
        const errors = result && result.userErrors;
        if (errors && errors.length > 0) {
          console.warn('[Click & Collect] cartBuyerIdentityUpdate userErrors:', errors);
          return null;
        }
        if (!result || !result.cart) {
          console.warn('[Click & Collect] cartBuyerIdentityUpdate: unexpected response (check token validity and cart ID):', data);
          return null;
        }
        const checkoutUrl = result.cart.checkoutUrl || null;
        console.log('[Click & Collect] cartBuyerIdentityUpdate → deliveryMethod:', deliveryMethod, locationId ? '| locationId: ' + locationId : '', checkoutUrl ? '| checkoutUrl obtained ✓' : '| no checkoutUrl');
        return checkoutUrl;
      })
      .catch((err) => {
        console.error('[Click & Collect] cartBuyerIdentityUpdate error:', err);
        return null;
      });
  }

  /**
   * Finds the first SHIPPING-type delivery option handle on the cart.
   * Called when switching back to Standard Delivery so the checkout doesn't
   * remain stuck on the previously committed pickup option.
   */
  function fetchShippingOptionHandle(cartGid, shopDomain, storefrontToken) {
    const query =
      'query getCartDeliveryGroups($cartId: ID!) {' +
      '  cart(id: $cartId) {' +
      '    deliveryGroups(first: 5) {' +
      '      edges {' +
      '        node {' +
      '          id' +
      '          deliveryOptions {' +
      '            handle' +
      '            title' +
      '            deliveryMethodType' +
      '          }' +
      '        }' +
      '      }' +
      '    }' +
      '  }' +
      '}';

    return fetch('https://' + shopDomain + '/api/' + STOREFRONT_API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontToken,
      },
      body: JSON.stringify({ query: query, variables: { cartId: cartGid } }),
    })
      .then((r) => r.json())
      .then((data) => {
        const edges =
          data.data &&
          data.data.cart &&
          data.data.cart.deliveryGroups &&
          data.data.cart.deliveryGroups.edges;

        if (!edges || edges.length === 0) return null;

        for (var i = 0; i < edges.length; i++) {
          var group = edges[i].node;
          var options = group.deliveryOptions || [];
          for (var j = 0; j < options.length; j++) {
            var opt = options[j];
            if (opt.deliveryMethodType === 'SHIPPING') {
              console.log('[Click & Collect] fetchShippingOptionHandle → matched:', opt.title, '| handle:', opt.handle);
              return { groupId: group.id, handle: opt.handle };
            }
          }
        }

        console.log('[Click & Collect] fetchShippingOptionHandle → no SHIPPING option found');
        return null;
      })
      .catch(function (err) {
        console.error('[Click & Collect] fetchShippingOptionHandle error:', err);
        return null;
      });
  }

  /**
   * Commits a specific delivery option handle on the cart via cartSelectedDeliveryOptionsUpdate.
   * Used for the standard-delivery flow to explicitly select a shipping rate.
   */
  function selectCartDeliveryOption(cartGid, shopDomain, storefrontToken, deliveryGroupId, deliveryOptionHandle) {
    const mutation =
      'mutation cartSelectedDeliveryOptionsUpdate($cartId: ID!, $selectedDeliveryOptions: [CartSelectedDeliveryOptionInput!]!) {' +
      '  cartSelectedDeliveryOptionsUpdate(cartId: $cartId, selectedDeliveryOptions: $selectedDeliveryOptions) {' +
      '    cart { id }' +
      '    userErrors { field message }' +
      '  }' +
      '}';

    return fetch('https://' + shopDomain + '/api/' + STOREFRONT_API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontToken,
      },
      body: JSON.stringify({
        query: mutation,
        variables: {
          cartId: cartGid,
          selectedDeliveryOptions: [
            { deliveryGroupId: deliveryGroupId, deliveryOptionHandle: deliveryOptionHandle },
          ],
        },
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        var errors =
          data.data &&
          data.data.cartSelectedDeliveryOptionsUpdate &&
          data.data.cartSelectedDeliveryOptionsUpdate.userErrors;
        if (errors && errors.length > 0) {
          console.warn('[Click & Collect] cartSelectedDeliveryOptionsUpdate userErrors:', errors);
        } else {
          console.log('[Click & Collect] cartSelectedDeliveryOptionsUpdate → selected handle:', deliveryOptionHandle);
        }
      })
      .catch(function (err) {
        console.error('[Click & Collect] cartSelectedDeliveryOptionsUpdate error:', err);
      });
  }

  /**
   * Commits the pickup delivery option on the cart via cartSelectedDeliveryOptionsUpdate.
   * Called after cartBuyerIdentityUpdate so the cart's selectedDeliveryOption reflects
   * the new store immediately. Without this, the previously-committed Store A wins at
   * checkout even after the buyerIdentity preference is updated to Store B.
   * Matches the PICK_UP delivery option by title (normalized) per Shopify support guidance.
   */
  function commitPickupSelection(cartGid, shopDomain, storefrontToken, storeName, locationId) {
    var normalized = storeName.trim().toLowerCase();
    var cleanId = locationId ? String(locationId).replace(/\D/g, '') : '';
    var query =
      'query GetCartPickupOptions($cartId: ID!) {' +
      '  cart(id: $cartId) {' +
      '    deliveryGroups(first: 5) {' +
      '      edges { node { id deliveryOptions { handle title deliveryMethodType } } }' +
      '    }' +
      '  }' +
      '}';

    return fetch('https://' + shopDomain + '/api/' + STOREFRONT_API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontToken,
      },
      body: JSON.stringify({ query: query, variables: { cartId: cartGid } }),
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var edges = (data && data.data && data.data.cart && data.data.cart.deliveryGroups && data.data.cart.deliveryGroups.edges) || [];
        for (var i = 0; i < edges.length; i++) {
          var node = edges[i].node;
          var options = node.deliveryOptions || [];
          for (var j = 0; j < options.length; j++) {
            var o = options[j];
            if (o.deliveryMethodType !== 'PICK_UP') continue;
            // Match by location ID first (strips non-digits to handle both plain IDs and GIDs).
            // Fall back to normalised title comparison for stores without a locationId.
            var handleId = String(o.handle).replace(/\D/g, '');
            var matchById = cleanId && handleId === cleanId;
            var matchByName = (o.title || '').trim().toLowerCase() === normalized;
            if (matchById || matchByName) {
              console.log('[Click & Collect] commitPickupSelection → matched:', o.title, '| handle:', o.handle, matchById ? '(by ID)' : '(by name)');
              return selectCartDeliveryOption(cartGid, shopDomain, storefrontToken, node.id, o.handle);
            }
          }
        }
        console.warn('[Click & Collect] commitPickupSelection: no PICK_UP option matched storeName="' + storeName + '" locationId="' + locationId + '"');
      })
      .catch(function (err) {
        console.error('[Click & Collect] commitPickupSelection error:', err);
      });
  }

  /**
   * Restore the previously selected store. Checks sessionStorage first
   * (fast, no network call). Falls back to cart attributes so the selection
   * also survives opening a new tab or a hard refresh.
   */
  function readStoreFromCartNote() {
    try {
      const name = sessionStorage.getItem(SESSION_KEY);
      if (name) {
        const locationId = sessionStorage.getItem(SESSION_KEY_ID) || null;
        return Promise.resolve({ name, locationId });
      }
    } catch (e) {}
    return fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        const name = (cart.attributes && cart.attributes._pickup_location) || null;
        if (!name) return null;
        const locationId = (cart.attributes && cart.attributes._pickup_location_id) || null;
        return { name, locationId };
      })
      .catch(() => null);
  }

  /* ── popup rendering ───────────────────────────────────────── */
  function renderStoreList(stores) {
    const body = qs('.cc-popup__body');
    if (!body) return;

    if (stores === null) {
      body.innerHTML = '<div class="cc-popup__loading">Loading stores…</div>';
      return;
    }

    if (stores.length === 0) {
      body.innerHTML =
        '<div class="cc-popup__empty">No stores available for all items in your cart.</div>';
      return;
    }

    const html = `<ul class="cc-store-list">${stores
      .map(
        (s, i) => `
      <li class="cc-store${selectedStore && selectedStore.name === s.name ? ' is-selected' : ''}"
          data-store-index="${i}">
        <label class="cc-store__info" for="cc-store-${i}">
          <h4 class="cc-store__name">${s.name}</h4>
          <div class="cc-store__address">${s.address}${s.distance != null ? `<span class="cc-store__distance">${s.distance} miles</span>` : ''}</div>
        </label>
      </li>`
      )
      .join('')}</ul>`;
    body.innerHTML = html;

    // If a store is already selected, show its detail panel
    if (selectedStore) {
      showStoreDetail(selectedStore);
    }
  }

  /* ── detail panel ─────────────────────────────────────────────── */
  function isMobile() {
    return window.innerWidth <= 749;
  }

  function removeMobileInlineDetail() {
    qsa('.cc-store__inline-detail').forEach(function (el) { el.remove(); });
  }

  function buildDetailHTML(store) {
    return '<div class="cc-detail__name">' + store.name + '</div>' +
      '<div class="cc-detail__address">' + (store.address || store.addressText || '') + '</div>' +
      '<div class="cc-detail__map" id="ClickCollectMapInline"></div>' +
      '<div class="cc-detail__hours">' +
        '<h4 class="cc-detail__hours-title">Opening Times</h4>' +
        '<ul class="cc-detail__hours-list">' +
          '<li><span class="cc-detail__day">Monday</span><span class="cc-detail__time">10:00 - 18:00</span></li>' +
          '<li><span class="cc-detail__day">Tuesday</span><span class="cc-detail__time">10:00 - 18:00</span></li>' +
          '<li><span class="cc-detail__day">Wednesday</span><span class="cc-detail__time">10:00 - 18:00</span></li>' +
          '<li><span class="cc-detail__day">Thursday</span><span class="cc-detail__time">10:00 - 18:00</span></li>' +
          '<li><span class="cc-detail__day">Friday</span><span class="cc-detail__time">10:00 - 18:00</span></li>' +
          '<li><span class="cc-detail__day">Saturday</span><span class="cc-detail__time">10:00 - 18:00</span></li>' +
          '<li><span class="cc-detail__day">Sunday</span><span class="cc-detail__time">10:00 - 18:00</span></li>' +
        '</ul>' +
      '</div>' +
      '<button type="button" class="cc-detail__select-btn">Select This Store</button>';
  }

  function showStoreDetail(store) {
    const popup = qs('#' + POPUP_ID);
    if (!popup) return;

    if (isMobile()) {
      // Mobile: inject detail inside the selected store card
      removeMobileInlineDetail();
      const selectedEl = qs('.cc-store.is-selected');
      if (!selectedEl) return;

      var inlineDetail = document.createElement('div');
      inlineDetail.className = 'cc-store__inline-detail';
      inlineDetail.innerHTML = buildDetailHTML(store);
      selectedEl.appendChild(inlineDetail);

      // Render map in the inline container
      renderDetailMap(store, inlineDetail.querySelector('#ClickCollectMapInline'));

      // Scroll the card into view
      selectedEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else {
      // Desktop: use the side panel
      popup.classList.add('has-detail');
      const detail = qs('#ClickCollectDetail');
      if (!detail) return;

      var nameEl = detail.querySelector('.cc-detail__name');
      if (nameEl) nameEl.textContent = store.name;

      var addrEl = detail.querySelector('.cc-detail__address');
      if (addrEl) addrEl.innerHTML = store.address || store.addressText || '';

      renderDetailMap(store, qs('#ClickCollectMap'));
    }
  }

  function hideStoreDetail() {
    const popup = qs('#' + POPUP_ID);
    if (popup) popup.classList.remove('has-detail');
    removeMobileInlineDetail();
  }

  function renderDetailMap(store, mapContainer) {
    if (!mapContainer) mapContainer = qs('#ClickCollectMap');
    if (!mapContainer) return;

    loadStoreCoords().then(function (coords) {
      let lat = null;
      let lng = null;

      // Match by location ID first, fall back to name substring match
      if (coords && coords.length > 0) {
        for (let i = 0; i < coords.length; i++) {
          if (coords[i].lat == null || coords[i].lng == null) continue;
          if (store.locationId && coords[i].id && String(coords[i].id) === String(store.locationId)) {
            lat = coords[i].lat; lng = coords[i].lng; break;
          }
        }
        if (lat == null && store.name) {
          const upper = store.name.trim().toUpperCase();
          for (let i = 0; i < coords.length; i++) {
            if (coords[i].lat == null || coords[i].lng == null || !coords[i].name) continue;
            const cName = coords[i].name.trim().toUpperCase();
            if (cName === upper || upper.includes(cName) || cName.includes(upper)) {
              lat = coords[i].lat; lng = coords[i].lng; break;
            }
          }
        }
      }

      if (lat != null && lng != null) {
        mapContainer.innerHTML =
          '<iframe src="https://www.openstreetmap.org/export/embed.html?bbox=' +
          (lng - 0.02) + '%2C' + (lat - 0.015) + '%2C' + (lng + 0.02) + '%2C' + (lat + 0.015) +
          '&layer=mapnik&marker=' + lat + '%2C' + lng +
          '" allowfullscreen></iframe>';
      } else {
        var q = encodeURIComponent(store.addressText || store.name);
        mapContainer.innerHTML =
          '<iframe src="https://www.openstreetmap.org/export/embed.html?bbox=106.7%2C-6.5%2C107.0%2C-6.3&layer=mapnik" allowfullscreen></iframe>';
      }
    });
  }

  /**
   * Filter stores by pincode / city. If the input looks like a
   * postcode or place name, geocode it and show only stores within
   * SEARCH_RADIUS_KM, sorted by distance.
   */
  function filterStores(query) {
    const q = query.trim();

    // Hide detail panel when starting a new search
    selectedStore = confirmedStore;
    hideStoreDetail();

    if (!q) {
      filteredStores = allStores.map((s) => ({ ...s, distance: null }));
      renderStoreList(filteredStores);
      return;
    }

    // Show loading while geocoding
    const body = qs('.cc-popup__body');
    if (body) body.innerHTML = '<div class="cc-popup__loading">Searching stores near you…</div>';

    console.log('[Click & Collect] filterStores → query:', q);
    Promise.all([geocodePincode(q), loadStoreCoords()])
      .then(([userLoc, coords]) => {
        console.log('[Click & Collect] filterStores → userLoc:', userLoc, '| coords entries:', coords ? coords.length : 0);
        if (!userLoc) {
          // Geocoding failed – fall back to text matching
          filteredStores = allStores
            .filter(
              (s) =>
                s.name.toLowerCase().includes(q.toLowerCase()) ||
                s.addressText.toLowerCase().includes(q.toLowerCase())
            )
            .map((s) => ({ ...s, distance: null }));
          renderStoreList(filteredStores);
          return;
        }

        // If no metafield coords at all, show all stores (no distance filtering)
        if (!coords || coords.length === 0) {
          console.warn('[Click & Collect] No store coordinates found in shop metafield custom.locations_mapping. Showing all stores without distance filter.');
          filteredStores = allStores.map((s) => ({ ...s, distance: null }));
          renderStoreList(filteredStores);
          return;
        }

        // Match a store to its coords entry.
        // ID match (from data-location-id injected by Liquid) is tried first — exact and reliable.
        // Falls back to name substring match for legacy { name, lat, lng } metafield entries.
        function findCoords(store) {
          for (let i = 0; i < coords.length; i++) {
            if (coords[i].lat == null || coords[i].lng == null) continue;
            if (store.locationId && coords[i].id && String(coords[i].id) === String(store.locationId)) {
              return coords[i];
            }
          }
          if (!store.name) return null;
          const upper = store.name.trim().toUpperCase();
          for (let i = 0; i < coords.length; i++) {
            if (coords[i].lat == null || coords[i].lng == null || !coords[i].name) continue;
            const cName = coords[i].name.trim().toUpperCase();
            if (cName === upper || upper.includes(cName) || cName.includes(upper)) {
              return coords[i];
            }
          }
          return null;
        }

        // Calculate distance for every available store
        const nearby = [];
        const unmatched = [];

        allStores.forEach((store) => {
          const c = findCoords(store);
          if (!c) {
            console.warn('[Click & Collect] No coordinates for store "' + store.name + '". Add it to custom.locations_mapping metafield.');
            unmatched.push({ ...store, distance: null });
            return;
          }
          const mi = haversineMiles(userLoc.lat, userLoc.lng, c.lat, c.lng);
          const dist = Math.round(mi * 10) / 10;
          console.log('[Click & Collect] filterStores → store:', store.name, '| coordsMatch:', c.name, '| lat:', c.lat, 'lng:', c.lng, '| distance:', dist, 'miles', dist <= SEARCH_RADIUS_MILES ? '✓ NEARBY' : '✗ TOO FAR');
          if (dist <= SEARCH_RADIUS_MILES) {
            nearby.push({ ...store, distance: dist });
          }
        });

        nearby.sort((a, b) => a.distance - b.distance);

        console.log('[Click & Collect] filterStores → nearby:', nearby.length, '| unmatched:', unmatched.length, '(hidden)');
        // Only show stores within the radius; hide unmatched and out-of-range stores
        filteredStores = nearby;

        if (filteredStores.length === 0) {
          if (body)
            body.innerHTML =
              '<div class="cc-popup__empty">No stores found within ' +
              SEARCH_RADIUS_MILES +
              ' miles of this location.</div>';
          return;
        }

        renderStoreList(filteredStores);
      })
      .catch(() => {
        filteredStores = allStores.map((s) => ({ ...s, distance: null }));
        renderStoreList(filteredStores);
      });
  }

  /* ── popup open / close ────────────────────────────────────── */
  function openPopup() {
    const popup = qs('#' + POPUP_ID);
    const overlay = qs('#' + OVERLAY_ID);
    if (popup) popup.classList.add('is-active');
    if (overlay) overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';

    if (!fetched) {
      renderStoreList(null);
      loadStores();
    }
  }

  function closePopup() {
    const popup = qs('#' + POPUP_ID);
    const overlay = qs('#' + OVERLAY_ID);
    if (popup) {
      popup.classList.remove('is-active');
      popup.classList.remove('has-detail');
    }
    if (overlay) overlay.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  function loadStores() {
    const gen = ++fetchGeneration;
    const rootUrl = getRootUrl();

    getCartItems()
      .then((items) => {
        if (gen !== fetchGeneration) return; // cart changed again, discard stale results

        if (items.length === 0) {
          allStores = [];
          filteredStores = [];
          fetched = true;
          renderStoreList([]);
          return;
        }

        const promises = items.map((item) =>
          fetchStoresForVariant(item.variantId, rootUrl)
        );

        return Promise.all(promises).then((results) => {
          if (gen !== fetchGeneration) return; // superseded, discard

          allStores = intersectStores(results);
          filteredStores = allStores;
          fetched = true;

          // If confirmedStore was restored from cart attributes without a locationId
          // (e.g. cross-tab / hard-refresh fallback), back-fill it from the fresh store list.
          if (confirmedStore && !confirmedStore.locationId) {
            const match = allStores.find((s) => s.name === confirmedStore.name);
            if (match && match.locationId) {
              confirmedStore.locationId = match.locationId;
              if (selectedStore && selectedStore.name === confirmedStore.name) {
                selectedStore.locationId = match.locationId;
              }
              // Persist the now-complete locationId so future reloads don't need to back-fill
              try { sessionStorage.setItem(SESSION_KEY_ID, String(match.locationId)); } catch (e) {}
            }
          }

          // If the previously confirmed store is no longer available for the
          // new cart contents, clear the selection so the user picks again.
          if (confirmedStore && !allStores.some((s) => s.name === confirmedStore.name)) {
            confirmedStore = null;
            selectedStore = null;
            hideSelectedStoreBanner();
            clearStoreFromCartNote();
            const stdRadio = qs('#delivery-standard');
            if (stdRadio) stdRadio.checked = true;
          }

          renderStoreList(filteredStores);
        });
      })
      .catch(() => {
        if (gen !== fetchGeneration) return;
        allStores = [];
        filteredStores = [];
        fetched = true;
        renderStoreList([]);
      });
  }

  /* ── selected store banner (inside .js-contents, gets rebuilt) */
  function showSelectedStoreBanner() {
    const banner = qs('#' + SELECTED_STORE_ID);
    if (!banner || !selectedStore) return;
    banner.querySelector('.cc-selected-store__name').textContent = selectedStore.name;
    banner.classList.add('is-visible');
  }

  function hideSelectedStoreBanner() {
    const banner = qs('#' + SELECTED_STORE_ID);
    if (banner) banner.classList.remove('is-visible');
  }

  /**
   * After a cart AJAX re‑render the radios and banner are fresh DOM.
   * Restore the visual state from our in‑memory `selectedStore`.
   */
  function restoreStateAfterRerender() {
    if (selectedStore) {
      const collectRadio = qs('#delivery-collect');
      if (collectRadio) collectRadio.checked = true;
      showSelectedStoreBanner();
    }
  }

  /* ── event delegation (attached once, survives re‑renders) ─── */
  function attachGlobalListeners() {
    if (listenersAttached) return;
    listenersAttached = true;

    /* delivery radio changes (delegated) */
    document.addEventListener('change', (e) => {
      if (e.target.matches('#delivery-collect')) {
        openPopup();
      }
      if (e.target.matches('#delivery-standard')) {
        selectedStore = null;
        confirmedStore = null;
        hideSelectedStoreBanner();
        clearStoreFromCartNote();
        getCartToken().then(function (token) {
          var popup = document.getElementById(POPUP_ID);
          var shopDomain = popup ? popup.dataset.shopDomain : null;
          var storefrontToken = popup ? popup.dataset.storefrontToken : null;

          updateCartDeliveryPreference(token, 'SHIPPING', null);

          if (shopDomain && storefrontToken && token) {
            var cartGid = 'gid://shopify/Cart/' + token;
            fetchShippingOptionHandle(cartGid, shopDomain, storefrontToken)
              .then(function (result) {
                if (result) {
                  selectCartDeliveryOption(cartGid, shopDomain, storefrontToken, result.groupId, result.handle);
                }
              });
          }
        });
      }
    });

    /* click delegation for popup elements */
    document.addEventListener('click', (e) => {
      /* overlay */
      if (e.target.id === OVERLAY_ID) {
        selectedStore = confirmedStore;
        closePopup();
        if (!confirmedStore) {
          const stdRadio = qs('#delivery-standard');
          if (stdRadio) stdRadio.checked = true;
        }
        return;
      }

      /* close button */
      if (e.target.closest('.cc-popup__close')) {
        selectedStore = confirmedStore;
        closePopup();
        if (!confirmedStore) {
          const stdRadio = qs('#delivery-standard');
          if (stdRadio) stdRadio.checked = true;
        }
        return;
      }

      /* detail panel select button (check BEFORE store item) */
      if (e.target.closest('.cc-detail__select-btn')) {
        if (!selectedStore) return;
        confirmedStore = selectedStore;
        // Close popup and show banner FIRST (synchronously), then save in the
        // background. This ensures the popup is no longer "is-active" before
        // saveStoreToCartNote calls cart/update.js — otherwise the cart update
        // triggers PUB_SUB_EVENTS.cartUpdate while the popup is still open,
        // which causes our own handler to call loadStores(), which can race
        // and wipe the confirmed store via clearStoreFromCartNote().
        showSelectedStoreBanner();
        closePopup();
        const storeForPreference = selectedStore;
        var ccPopup = document.getElementById(POPUP_ID);
        var ccShopDomain = ccPopup ? ccPopup.dataset.shopDomain : null;
        var ccStorefrontToken = ccPopup ? ccPopup.dataset.storefrontToken : null;
        saveStoreToCartNote(storeForPreference.name, storeForPreference.locationId).then(function () {
          return getCartToken();
        }).then(function (token) {
          return updateCartDeliveryPreference(token, 'PICK_UP', storeForPreference.locationId)
            .then(function () {
              if (ccShopDomain && ccStorefrontToken && token) {
                return commitPickupSelection(
                  'gid://shopify/Cart/' + token,
                  ccShopDomain,
                  ccStorefrontToken,
                  storeForPreference.name,
                  storeForPreference.locationId
                );
              }
            });
        });
        return;
      }

      /* store list item */
      const storeEl = e.target.closest('.cc-store');
      if (storeEl) {
        if (e.target.closest('a')) return;
        if (e.target.closest('.cc-store__inline-detail')) return;
        const idx = parseInt(storeEl.dataset.storeIndex, 10);
        selectedStore = filteredStores[idx];
        qsa('.cc-store').forEach((el) => el.classList.remove('is-selected'));
        storeEl.classList.add('is-selected');
        showStoreDetail(selectedStore);
        return;
      }

      /* search button */
      if (e.target.closest('.cc-popup__search-btn')) {
        const input = qs('.cc-popup__search-input');
        if (input) filterStores(input.value);
        return;
      }

      /* change store link */
      if (e.target.closest('.cc-selected-store__change')) {
        openPopup();
        return;
      }
    });

    /* keyboard */
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.classList.contains('cc-popup__search-input')) {
        e.preventDefault();
        filterStores(e.target.value);
      }
      if (e.key === 'Escape') {
        closePopup();
        if (!selectedStore) {
          const stdRadio = qs('#delivery-standard');
          if (stdRadio) stdRadio.checked = true;
        }
      }
    });

    /* cart form checkout interception – redirects to Storefront API checkoutUrl
       so Shopify opens checkout with the Pickup tab pre-selected */
    document.addEventListener('submit', function (e) {
      var submitter = e.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      if (!confirmedStore) return;

      var popup = document.getElementById(POPUP_ID);
      var storefrontToken = popup ? popup.dataset.storefrontToken : null;
      if (!storefrontToken) return; // token not set – let form submit normally

      e.preventDefault();

      // Step 1: Write cart attributes synchronously BEFORE calling cartBuyerIdentityUpdate.
      // This prevents the race condition where checkout loads before /cart/update.js resolves
      // and the Shopify Function reads the stale _pickup_location attribute.
      submitter.disabled = true;
      var storeSnapshot = confirmedStore;
      var capturedToken = null;
      // Order per Shopify support: /cart/update.js → cartBuyerIdentityUpdate → cartSelectedDeliveryOptionsUpdate → redirect
      saveStoreToCartNote(storeSnapshot.name, storeSnapshot.locationId)
        .then(function () {
          return getCartToken();
        })
        .then(function (token) {
          capturedToken = token;
          return updateCartDeliveryPreference(token, 'PICK_UP', storeSnapshot.locationId);
        })
        .then(function (checkoutUrl) {
          if (!checkoutUrl) {
            submitter.disabled = false;
            e.target.submit();
            return;
          }
          var p = document.getElementById(POPUP_ID);
          var sd = p ? p.dataset.shopDomain : null;
          var st = p ? p.dataset.storefrontToken : null;
          if (capturedToken && sd && st) {
            return commitPickupSelection(
              'gid://shopify/Cart/' + capturedToken, sd, st, storeSnapshot.name, storeSnapshot.locationId
            ).then(function () {
              window.location.href = checkoutUrl;
            });
          }
          window.location.href = checkoutUrl;
        })
        .catch(function () {
          submitter.disabled = false;
          e.target.submit();
        });
    });

    /* subscribe to cart updates (Dawn pub/sub) */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        // Cancel any in-flight loadStores() by advancing the generation counter
        fetchGeneration++;
        fetched = false;
        allStores = [];
        filteredStores = [];

        // Fetch the updated cart immediately. If the cart is now empty (user
        // removed the last item), wipe C&C state right now — before the user
        // can navigate away — so it is never restored on the next page load.
        // If the cart still has items, leave the selection alone; loadStores()
        // validation will handle store availability when the popup is next opened.
        fetch('/cart.js')
          .then((r) => r.json())
          .then((cart) => {
            if (cart.items.length === 0 && confirmedStore) {
              confirmedStore = null;
              selectedStore = null;
              hideSelectedStoreBanner();
              clearStoreFromCartNote();
            }
            // Restore visual state AFTER the empty-cart check so that a cleared
            // selectedStore is not re-shown by restoreStateAfterRerender.
            setTimeout(restoreStateAfterRerender, 150);
          })
          .catch(() => {
            setTimeout(restoreStateAfterRerender, 150);
          });

        // If the popup is already open, refresh the store list immediately
        // so the user sees stores for the updated cart, not the old one.
        const popup = document.getElementById(POPUP_ID);
        if (popup && popup.classList.contains('is-active')) {
          renderStoreList(null);
          loadStores();
        }
      });
    }
  }

  /* ── init ──────────────────────────────────────────────────── */
  function init() {
    // Move overlay & popup to body so position:fixed works correctly
    // (parent elements with transform/isolate break fixed positioning)
    const overlay = document.getElementById(OVERLAY_ID);
    const popup = document.getElementById(POPUP_ID);
    if (overlay && overlay.parentNode !== document.body) document.body.appendChild(overlay);
    if (popup && popup.parentNode !== document.body) document.body.appendChild(popup);

    attachGlobalListeners();

    readStoreFromCartNote().then((stored) => {
      if (stored && stored.name) {
        selectedStore = { name: stored.name, address: '', addressText: '', locationId: stored.locationId };
        confirmedStore = selectedStore;
        const collectRadio = qs('#delivery-collect');
        if (collectRadio) collectRadio.checked = true;
        showSelectedStoreBanner();

        // Validate the restored store against the current cart in the background.
        // loadStores() will automatically clear confirmedStore and reset to
        // Standard Delivery if the store is no longer available for the current
        // cart items (e.g. the user deleted the product and added a different one).
        loadStores();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
